import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { PaperFilters } from '@/module/Author/Paper/model/PaperTypes';
import { PublicationNewAliasRequest } from '@/module/Author/Publication/model/PublicationTypes';
import { PublicationImpactFactor } from '@/module/Journal';

import { Id, PagedParams, PagedResponse, getQueryKey, invalidateAfterDelete } from '@feature/utils';

import * as api from './ReviewApi';
import { Review, ReviewRequest } from './ReviewTypes';

const reviewKey = getQueryKey('review');

export function getReviewImpactFactorOptions(reviewId: Id): UseSuspenseQueryOptions<PublicationImpactFactor> {
  return {
    queryKey: reviewKey.as([reviewId, 'metrics', 'impact-factor']),
    queryFn: () => api.getReviewImpactFactor(reviewId),
  };
}

export function getReviewPagedOptions(
  params: PagedParams<PaperFilters>,
): UseSuspenseQueryOptions<PagedResponse<Review>> {
  return {
    queryKey: reviewKey.paged(params),
    queryFn: () => api.getPaged(params),
  };
}

export function getReviewOptions(reviewId: Id): UseSuspenseQueryOptions<Review> {
  return {
    queryKey: reviewKey.one(reviewId),
    queryFn: () => api.get(reviewId),
  };
}

export function useReviewEdit(reviewId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ReviewRequest) => api.edit(reviewId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: reviewKey.root(),
      }),
  });
}

export function useReviewCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ReviewRequest) => api.create(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: reviewKey.root(),
      }),
  });
}

export function useReviewDelete(reviewId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteById(reviewId),
    onSuccess: () => invalidateAfterDelete(queryClient, reviewKey, reviewId),
  });
}

export function useReviewAddNewAlias() {
  const queryClient = useQueryClient();
  return useMutation<Review, Error, { reviewId: Id; request: PublicationNewAliasRequest }>({
    mutationFn: ({ reviewId, request }) => api.addNewAlias(reviewId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: reviewKey.root(),
      }),
  });
}
